import Timer16 from '@carbon/icons-react/es/timer/16';
import Timer20 from '@carbon/icons-react/es/timer/20';
import { Menu as AntMenu } from 'antd';
import moment from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  Configuration,
  TimesheetApprovalType,
} from 'api/timesheets/configuration/configuration';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { PageLoading } from 'components/atoms/loading';
import {
  CompactMenu,
  CompactMenuTitle,
} from 'components/atoms/menu/compact-menu';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { ReturnToCurrentWeek } from 'components/organisms/timesheets/common/return-to-current-week';
import { WeekRangePicker } from 'components/organisms/timesheets/date-pickers';
import { DrawerType } from 'components/organisms/timesheets/my-timesheet/constants';
import { MyTimesheet } from 'components/organisms/timesheets/my-timesheet/my-timesheets';
import { SecondaryBar } from 'components/organisms/timesheets/secondary-bar';
import { WeekPreset } from 'components/organisms/timesheets/types';
import { Argon } from 'components/templates';
import { date, DayOfWeek } from 'core/day-of-week';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useTimesheetGetRejectNote } from 'queries/timesheets/reject-note';
import { getDays } from 'utils/date-field/get-days-array';
import { useTimesheetUrlState } from './use-timesheet-url-state';

const Menu = styled(AntMenu)`
  background: var(--coolest-gray-10);
  padding: 0;
  border: 0;
`;

export const MenuItemStyled = styled(Menu.Item)`
  color: var(--gray-70) !important;

  svg {
    margin-right: var(--spacing-03) !important;
  }
`;

const ValidWeekPreset = [WeekPreset.ThisWeek, WeekPreset.PreviousWeek];
const WeekPresetAllowedInURLs = [WeekPreset.ThisWeek];

export const MyTimesheetPage: FC<{
  configuration: Configuration;
  pendingApprovalCount: number;
  hasApprovalTabPermission: boolean;
}> = ({ configuration, pendingApprovalCount, hasApprovalTabPermission }) => {
  const translate = useTranslate();
  const { push } = useHistory();
  const { user: currentUser } = useUser();
  const [visibleDrawer, setVisibleDrawer] = useState<DrawerType>();

  const { startDate: selectedDay, setStartDate: setSelectedDay } =
    useTimesheetUrlState({
      allowedWeekPresetsInUrl: WeekPresetAllowedInURLs,
      validWeekPresets: ValidWeekPreset,
      defaultStartDate: date(DayOfWeek.Monday, moment()),
      url: '/timesheets/:week/my-timesheet',
    });

  const isWeekFreezed = useMemo(() => {
    if (configuration.lockSetting.freeze.enabled) {
      const currentWeekDays = getDays(selectedDay, 7).map((x) =>
        x.format('YYYY-MM-DD'),
      );

      return (
        currentWeekDays.includes(
          configuration.lockSetting.freeze.enableAfter ?? '',
        ) || selectedDay < moment(configuration.lockSetting.freeze.enableAfter)
      );
    }

    return false;
  }, [
    configuration.lockSetting.freeze.enableAfter,
    configuration.lockSetting.freeze.enabled,
    selectedDay,
  ]);

  const isProjectOwnerApproval = useMemo(
    () => configuration.approvalSetting === TimesheetApprovalType.ProjectOwner,
    [configuration.approvalSetting],
  );

  const { data } = useTimesheetGetRejectNote(
    currentUser.userId,
    selectedDay.format('YYYY-MM-DD'),
  );

  return configuration == null ? (
    <PageLoading />
  ) : (
    <Argon
      title={
        <HeaderWithIconTitle
          icon={<Timer20 />}
          title={<FormattedMessage id="menu.label.timesheets" />}
        />
      }
      tabs={
        hasApprovalTabPermission ? (
          <CompactMenu
            mode="horizontal"
            selectedKeys={['my-timesheet']}
            onClick={({ key }) => {
              if (key === 'approvals') {
                push('/timesheets/this-week/approvals');
              }
            }}
          >
            <CompactMenu.Item key="my-timesheet" data-cy="tabs.my-timesheets">
              <CompactMenuTitle
                icon={<Timer16 />}
                text={<FormattedMessage id="timesheets.tabs.my_timesheets" />}
              />
            </CompactMenu.Item>
            <CompactMenu.Item key="approvals" data-cy="tabs.approvals">
              <CompactMenuTitle
                icon={<ApproveOutline16 />}
                text={
                  <FormattedMessage
                    id="timesheets.tabs.approvals"
                    values={{ count: pendingApprovalCount }}
                  />
                }
              />
            </CompactMenu.Item>
          </CompactMenu>
        ) : null
      }
      actions={{
        primary: (
          <>
            <WeekRangePicker value={selectedDay} onChange={setSelectedDay} />
            <ReturnToCurrentWeek
              currentDate={selectedDay}
              onResetToDefaultWeek={(nextDate) => setSelectedDay(nextDate)}
            />
          </>
        ),
        secondary: (
          <SecondaryBar
            setVisibleDrawer={setVisibleDrawer}
            startDate={selectedDay}
            currentUser={currentUser}
            configuration={configuration}
            isWeekFreezed={isWeekFreezed}
            rejectNotes={data}
            isProjectOwnerApproval={isProjectOwnerApproval}
          />
        ),
      }}
      content={
        <>
          <Helmet>
            <title>{translate('timesheets.tabs.my_timesheets')}</title>
          </Helmet>
          <MyTimesheet
            visibleDrawer={visibleDrawer}
            setVisibleDrawer={setVisibleDrawer}
            startDate={selectedDay}
            configuration={configuration}
            currentUser={currentUser}
            isWeekFreezed={isWeekFreezed}
            rejectNotes={data}
            isProjectOwnerApproval={isProjectOwnerApproval}
          />
        </>
      }
    />
  );
};
